import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { BadgeStrip } from "../components/BadgeStrip";
import { RatingsTable } from "../components/RatingsTable";
import { AnalystList } from "../components/AnalystList";
import { CommitmentsCard } from "../components/CommitmentsCard";
import { KPI } from "../components/KPI";
import { NavLink } from "../components/NavLink";
import { fmtUSD, toNumber, uniqueSorted } from "../utils/format";

import { getDeals, searchDeals } from "../services/deals";
import {
  fetchKpis,
  fetchCommitments,
  fetchPlatformStats,
  fetchMomentumStats,
} from "../services/stats";
import { importDealsFile } from "../services/admin";


type SelectProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
};

type DealRow = {
  company?: string;
  platform?: string;
  industry?: string;
  status?: string;
  momentum?: string;
  amount_raised?: number | string;
  // allow extra fields from backend:
  [key: string]: unknown;
};

type SearchResult = {
  items: DealRow[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
};

type KPIs = {
  active_deals: number;
  total_deals: number;
  total_raised: number;
  avg_raise: number;
};

type Commitments = {
  months: string[];
  series: number[];
};

type PlatformStat = {
  platform: string;
  total_raised: number;
};

type MomentumStat = {
  momentum: string;
  count: number;
};

// ---------- Components ----------
function Select({ label, value, onChange, options }: SelectProps) {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-800 shadow-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
      >
        <option value="">All</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}

// main Dashboard page
export default function IncubatorInvestmentDashboard() {
  const [rows, setRows] = useState<DealRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [importing, setImporting] = useState<boolean>(false);
  const [importError, setImportError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [search, setSearch] = useState<string>("");
  const [platform, setPlatform] = useState<string>("");
  const [industry, setIndustry] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [momentum, setMomentum] = useState<string>("");
  const [sortKey, setSortKey] = useState<string>("amount_raised");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState<number>(1);
  const pageSize = 10;

  const [searchResult, setSearchResult] = useState<SearchResult>({
    items: [],
    total: 0,
    page: 1,
    page_size: pageSize,
    total_pages: 1,
  });

  const [kpis, setKpis] = useState<KPIs | null>(null);
  const [commitments, setCommitments] = useState<Commitments>({
    months: [],
    series: [],
  });
  const [platformStats, setPlatformStats] = useState<PlatformStat[]>([]);
  const [momentumStats, setMomentumStats] = useState<MomentumStat[]>([]);

  const loadDeals = useCallback((signal: AbortSignal) => {
    setLoading(true);
    setError(null);

    return getDeals({ delay: 800, signal } as any)
      .then((deals) => {
        setRows(deals as DealRow[]);
      })
      .catch((err: any) => {
        if (err.name !== "AbortError") {
          console.error(err);
          setError("Failed to load deals.");
        }
      })
      .finally(() => {
        if (!signal.aborted) {
          setLoading(false);
        }
      });
  }, []);

  const loadStats = useCallback((signal: AbortSignal) => {
    const opts = { delay: 400, signal } as any;

    fetchKpis(opts)
      .then(setKpis)
      .catch((err: any) => console.error("KPIs error:", err));

    fetchCommitments(opts)
      .then(setCommitments)
      .catch((err: any) => console.error("Commitments error:", err));

    fetchPlatformStats(opts)
      .then(setPlatformStats)
      .catch((err: any) => console.error("Platform stats error:", err));

    fetchMomentumStats(opts)
      .then(setMomentumStats)
      .catch((err: any) => console.error("Momentum stats error:", err));
  }, []);

  const loadSearch = useCallback(
    (signal: AbortSignal) =>
      searchDeals({
        search,
        platform,
        industry,
        status,
        momentum,
        sortKey,
        sortDir,
        page,
        pageSize,
        delay: 0,
        signal,
      } as any)
        .then((res: SearchResult) => {
          setSearchResult(res);
        })
        .catch((err: any) => {
          if (err.name !== "AbortError") {
            console.error("Search error:", err);
          }
        }),
    [search, platform, industry, status, momentum, sortKey, sortDir, page, pageSize]
  );

  // -------- load deals --------
  useEffect(() => {
    const controller = new AbortController();
    loadDeals(controller.signal);
    return () => controller.abort();
  }, [loadDeals]);

  // -------- load stats --------
  useEffect(() => {
    const controller = new AbortController();
    loadStats(controller.signal);
    return () => controller.abort();
  }, [loadStats]);

  // -------- filter options --------
  const platforms = useMemo(
    () => uniqueSorted(rows.map((r) => r.platform)),
    [rows]
  );
  const industries = useMemo(
    () => uniqueSorted(rows.map((r) => r.industry)),
    [rows]
  );
  const statuses = useMemo(
    () => uniqueSorted(rows.map((r) => r.status)),
    [rows]
  );
  const momenta = useMemo(
    () => uniqueSorted(rows.map((r) => r.momentum)),
    [rows]
  );

  // -------- search --------
  useEffect(() => {
    const controller = new AbortController();
    loadSearch(controller.signal);
    return () => controller.abort();
  }, [loadSearch]);

  const totalVisible = searchResult.total;

  useEffect(() => {
    setPage(1);
  }, [search, platform, industry, status, momentum]);

  const onSort = (k: string) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(k);
      setSortDir("desc");
    }
  };

  const triggerImport = () => {
    fileInputRef.current?.click();
  };

  const onImportFile = async (file: File) => {
    setImportError(null);
    setImporting(true);
    try {
      await importDealsFile(file);

      const controller = new AbortController();
      await Promise.all([
        loadDeals(controller.signal),
        loadStats(controller.signal),
        loadSearch(controller.signal),
      ]);
    } catch (err: any) {
      setImportError(err?.message || "Import failed.");
    } finally {
      setImporting(false);
    }
  };

  const totalPages = searchResult.total_pages || 1;
  const pageRows = searchResult.items || [];

  // badges
  const badges = useMemo(() => {
    const hotDeals = rows.filter(
      (r) => (r.momentum || "").toLowerCase() === "hot" && r.company
    );

    if (!hotDeals.length) {
      // fallback demo badges if the dataset has no "Hot" momentum yet
      return [
        "Hot Raises",
        "Faxo $68,731",
        "GACW $1,758,703",
        "StartEngine $2,225,363",
        "Care Access PBC $37,220",
        "LIFT Aircraft $693,388",
      ];
    }

    const items = hotDeals.map((d) => {
      const amount = fmtUSD(toNumber(d.amount_raised) || 0);
      return `${d.company} ${amount}`;
    });

    return ["Hot Raises", ...items];
  }, [rows]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="rounded-xl bg-white px-4 py-3 shadow text-sm text-slate-700">
          Loading incubator deals…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="rounded-xl bg-white px-4 py-3 shadow text-sm text-red-600">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Top nav */}
      <header className="border-b bg-white/80 backdrop-blur">
        <div className="mx-auto max-w-7xl px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-tr from-indigo-600 to-sky-500 text-white text-xs font-bold">
              IN
            </div>
            <div>
              <div className="text-sm font-semibold tracking-tight">
                Incubator Dashboard
              </div>
              <div className="text-xs text-slate-500">
                Deal intelligence for early-stage startups
              </div>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-5 text-xs text-slate-600">
            <NavLink label="Companies" active />
            <NavLink label="Funds" active={false}/>
            <NavLink label="Sectors" active={false}/>
            <NavLink label="Insights" active={false}/>
            <NavLink label="My Pipeline" active={false}/>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={triggerImport}
              className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:border-indigo-200 hover:text-indigo-600 disabled:opacity-40"
              aria-label="Import deals"
              title="Import deals"
              disabled={importing}
            >
              +
            </button>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".xlsx,.xls,.docx,.pdf"
              onChange={(e) => {
                const selected = e.target.files?.[0];
                e.target.value = "";
                if (selected) {
                  onImportFile(selected);
                }
              }}
            />
            <div className="hidden md:flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-xs font-semibold text-white">
              CR
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6 space-y-6">
        {/* Badge strip */}
        <BadgeStrip items={badges} />

        {/* KPIs (backend-derived) */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPI
            label="Active Deals"
            value={kpis ? kpis.active_deals.toLocaleString() : "—"}
          />
          <KPI
            label="Total Deals"
            value={kpis ? kpis.total_deals.toLocaleString() : "—"}
          />
          <KPI
            label="Capital Raised"
            value={kpis ? fmtUSD(kpis.total_raised) : "—"}
          />
          <KPI
            label="Avg. Raise / Deal"
            value={kpis ? fmtUSD(kpis.avg_raise) : "—"}
          />
        </section>

        {/* Filters */}
        <section className="rounded-2xl border border-slate-200 bg-white/80 p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="text-sm font-medium text-slate-800">
              Deal universe
              <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-normal text-slate-500">
                {totalVisible} visible
              </span>
            </div>
            {importError && (
              <div className="text-xs text-red-600">{importError}</div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm focus-within:ring-2 focus-within:ring-indigo-200">
                <span className="text-slate-400">🔍</span>
                <input
                  placeholder="Search company, platform, industry, state..."
                  className="w-full bg-transparent outline-none placeholder:text-slate-400"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            <Select
              label="Platform"
              value={platform}
              onChange={setPlatform}
              options={platforms}
            />
            <Select
              label="Industry"
              value={industry}
              onChange={setIndustry}
              options={industries}
            />
            <Select
              label="Status"
              value={status}
              onChange={setStatus}
              options={statuses}
            />
            <Select
              label="Momentum"
              value={momentum}
              onChange={setMomentum}
              options={momenta}
            />
          </div>
        </section>

        {/* Main grid: table + side cards */}
        <section className="grid grid-cols-1 lg:grid-cols-[minmax(0,2.1fr)_minmax(0,1fr)] gap-5">
          <div className="space-y-3">
            <RatingsTable
              rows={pageRows}
              onSort={onSort}
              sortKey={sortKey}
              sortDir={sortDir}
            />
            {/* Pagination under table */}
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>
                Page {page} of {totalPages}
              </span>
              <div className="flex gap-2">
                <button
                  className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 disabled:opacity-40"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </button>
                <button
                  className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 disabled:opacity-40"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </button>
              </div>
            </div>
          </div>

          <aside className="space-y-4">
            <AnalystList items={pageRows.slice(0, 6)} />

            <CommitmentsCard
              months={commitments.months}
              series={commitments.series}
            />

            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm">
              <div className="mb-3 flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-900">
                  Platform breakdown
                </div>
                <span className="text-xs text-slate-400">Top 4</span>
              </div>
              <div className="space-y-2">
                {platformStats.slice(0, 4).map((p) => (
                  <div
                    key={p.platform}
                    className="flex items-center justify-between text-xs"
                  >
                    <span className="text-slate-600">{p.platform}</span>
                    <span className="text-slate-500">
                      {fmtUSD(p.total_raised)}
                    </span>
                  </div>
                ))}
                {!platformStats.length && (
                  <div className="text-xs text-slate-400">
                    No platform data yet.
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm">
              <div className="mb-3 text-sm font-semibold text-slate-900">
                Momentum mix
              </div>
              <div className="space-y-2 text-xs">
                {momentumStats.map((m) => (
                  <div
                    key={m.momentum}
                    className="flex items-center justify-between"
                  >
                    <span className="text-slate-600">{m.momentum}</span>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700">
                      {m.count} deal{m.count === 1 ? "" : "s"}
                    </span>
                  </div>
                ))}
                {!momentumStats.length && (
                  <div className="text-xs text-slate-400">
                    No momentum data yet.
                  </div>
                )}
              </div>
            </div>
          </aside>
        </section>
      </main>
    </div>
  );
}
